import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { DetailedDriveStatistics } from '@/api/main/types.gen'
import { getTimezone } from '@/utils/datetime'
import { getSchedulingIntervalStart, getSchedulingIntervalEnd } from '@/utils/schedulingBounds'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Calculate the timeline span in minutes based on start and end hours
 * Handles cross-midnight case (e.g., 7:00 to 6:00 next day = 23 hours)
 */
function getTimelineSpanMinutes(): number {
  const startTime = getSchedulingIntervalStart()
  const endTime = getSchedulingIntervalEnd()

  const startMinutes = startTime.hours * 60 + startTime.minutes
  let endMinutes = endTime.hours * 60 + endTime.minutes

  // If end time is before start time, it's the next day (crosses midnight)
  if (endMinutes <= startMinutes) {
    endMinutes += 24 * 60 // Add 24 hours
  }

  return endMinutes - startMinutes
}

export const getDrivePosition =
  (timelineStart: dayjs.Dayjs) =>
  (drive: DetailedDriveStatistics): { top: string; height: string } => {
    // Parse timestamps as UTC (API returns UTC) then convert to configured timezone
    const timezone = getTimezone()
    const startPrague = dayjs.utc(drive.drive_started_at).tz(timezone)
    const endPrague = dayjs.utc(drive.drive_finished_at).tz(timezone)

    const timelineSpanMinutes = getTimelineSpanMinutes()
    const startDiff = startPrague.diff(timelineStart, 'minutes')
    const endDiff = endPrague.diff(timelineStart, 'minutes')

    // Handle cross-midnight drives
    let adjustedStartDiff = startDiff
    let adjustedEndDiff = endDiff

    if (endDiff < startDiff) {
      // Drive crosses midnight
      adjustedEndDiff += timelineSpanMinutes
    }

    // Clamp to timeline bounds
    const posStart = Math.max(0, Math.min(timelineSpanMinutes, adjustedStartDiff))
    const posEnd = Math.max(0, Math.min(timelineSpanMinutes, adjustedEndDiff))

    // No overlap with timeline
    if (posEnd <= posStart) {
      return { top: '0%', height: '0%' }
    }

    // Calculate position as percentage
    const top = (posStart / timelineSpanMinutes) * 100
    const height = ((posEnd - posStart) / timelineSpanMinutes) * 100

    return {
      top: `${top}%`,
      height: `${height}%`,
    }
  }

export const calculateTimeRange = (drives: DetailedDriveStatistics[]): { start: Date; end: Date } | null => {
  if (drives.length === 0) return null

  // Parse timestamps as UTC (API returns UTC) then convert to configured timezone
  const timezone = getTimezone()
  const startTimes = drives.map(i => dayjs.utc(i.drive_started_at).tz(timezone))
  const endTimes = drives.map(i => dayjs.utc(i.drive_finished_at).tz(timezone))

  const minStart = startTimes.reduce((min, current) => (current.isBefore(min) ? current : min), startTimes[0])
  const maxEnd = endTimes.reduce((max, current) => (current.isAfter(max) ? current : max), endTimes[0])

  return {
    start: minStart.toDate(),
    end: maxEnd.toDate(),
  }
}

export const generateTimeSlots = (): string[] => {
  const startTime = getSchedulingIntervalStart()
  const timelineSpanMinutes = getTimelineSpanMinutes()

  // Calculate number of slots (one per hour, plus one for the end time)
  // For example, 7:00 to 6:00 next day = 23 hours = 24 slots (7:00, 8:00, ..., 23:00, 0:00, 1:00, ..., 6:00)
  const numSlots = Math.ceil(timelineSpanMinutes / 60) + 1

  return Array.from({ length: numSlots }, (_, index) => {
    // Calculate hour from start time
    let totalMinutes = startTime.hours * 60 + startTime.minutes + index * 60
    let hour = Math.floor(totalMinutes / 60) % 24
    const minutes = totalMinutes % 60

    return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  })
}

export const getTimelineStart = (date: dayjs.Dayjs): dayjs.Dayjs => {
  const startTime = getSchedulingIntervalStart()
  return date.tz(getTimezone()).hour(startTime.hours).minute(startTime.minutes).second(0).millisecond(0)
}
